/**
 * Probable duplicates and the revision warning, shown in the preview before anything is written.
 *
 * A prepared manufacture is a probable duplicate of an existing one when it has the same product,
 * the same STORE business day and the same quantity, and the batch numbers do not contradict.
 * Batches contradict ONLY when both are non-empty and differ (B-77 vs B-78). Either side without
 * one is compatible:
 *  - the existing one has none: all of the hand-entered history has none, and a file that now
 *    adds one must still match it;
 *  - the new one has none: an earlier import carried batches and this file has the batch column
 *    unmapped or empty.
 * Either way, treating it as new would double the period on re-import. (ATL-1701 final review:
 * this deliberately extends the spec's one-sided "equal, or the existing has none" to the
 * symmetric rule.)
 * Each existing record is matched by at most one prepared manufacture (multiset matching).
 *
 * Existing manufactures come in two read shapes (`getManufacturesOnDateRange`):
 *  - Firestore: `product.id` (Firestore id), `store`, `date` Timestamp;
 *  - Supabase:  flat `productId` (the product's uuid), `storeId` (uuid), `date` ISO string.
 * Products are compared through the run's ManufactureProductIndex `byAnyId`, so a uuid and a
 * Firestore id of the same product are the same product.
 *
 * The store is NOT compared per record: the caller loads the existing manufactures for the run's
 * store (the read already filters by it), and a Supabase `storeId` is a uuid while the run's store
 * is a Firestore id. The business day of every record is computed for the run's store.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- manufacture/product docs are untyped across Firestore/Supabase read modes (same ruling as utils/salesImport/resolve.ts) */

import { format } from 'date-fns'

import { normalizeKey } from '../salesImport/resolve'
import { type ImportDate } from '../importCells'
import { asDate, getDailyStatID, getStoreBusinessDate } from '../timezoneUtils'
import { type ManufactureInfo } from './buildInfo'
import { atQuantityScale } from './parseRows'
import { type ManufactureProductIndex } from './resolve'

export interface NormalizedExistingManufacture {
  productId: string
  /** `store` (Firestore) or `storeId` (Supabase uuid); informational, see the module note. */
  storeId: string | null
  date: Date
  quantity: number
  batchNumber: string | null
}

/** The fields of a prepared info the comparison reads (a ManufactureInfo satisfies it). */
export type ComparableInfo = Pick<ManufactureInfo, 'product' | 'quantity' | 'date'> & {
  batchNumber?: string
}

export interface KeyedInfo {
  key: string
  info: ComparableInfo
}

export interface DuplicateResult {
  /**
   * Keys that match a manufacture already in the books — skipped by default. At most one key per
   * existing record; a twin that used up a record is here AND in `inRunTwins`.
   */
  existingMatch: Set<string>
  /** Keys identical to another prepared manufacture of this run — both kept, both flagged. */
  inRunTwins: Set<string>
}

const normalizeBatch = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Both read shapes → one comparable record. Returns `null` for a record that names no product,
 * no usable date or no finite quantity: it cannot be compared with anything, so it can neither
 * be, nor hide, a duplicate.
 */
export const normalizeExistingManufacture = (m: any): NormalizedExistingManufacture | null => {
  const productId = normalizeKey(m?.product?.id ?? m?.productId ?? m?.product_id)
  const date = asDate(m?.date)
  const quantity = typeof m?.quantity === 'number' ? m.quantity : Number.NaN
  if (productId === null || date === null || !Number.isFinite(quantity)) return null
  return {
    productId,
    storeId: normalizeKey(m?.store ?? m?.storeId ?? m?.store_id),
    date,
    quantity,
    batchNumber: normalizeBatch(m?.batchNumber),
  }
}

/** One identity per product across id spellings; an id outside the catalog stays itself. */
const productIdentity = (index: ManufactureProductIndex, rawId: unknown): string | null => {
  const id = normalizeKey(rawId)
  if (id === null) return null
  const product = index.byAnyId[id]
  return product ? (normalizeKey(product.id) ?? id) : id
}

const SEP = '\u0000'

export const findDuplicates = (
  infos: KeyedInfo[],
  existing: any[],
  store: string,
  index: ManufactureProductIndex
): DuplicateResult => {
  const dayKey = (productId: string | null, date: Date, quantity: number): string =>
    // At the numeric(10,4) scale: Firestore may hold a hand-entered 0.30000000000000004 that
    // Supabase stores as 0.3000, and both must match a 0.3 import in either read mode.
    `${productId}${SEP}${getDailyStatID(store, date)}${SEP}${atQuantityScale(quantity)}`

  // Existing records as a MULTISET per product+day+quantity: the batch number of each record,
  // one entry per record. Every match below removes the entry it uses, so M existing records
  // can flag at most M new manufactures — a real day can contain two identical runs, and with
  // duplicates skipped by default an unconsumed record would silently drop the second one.
  const pool = new Map<string, Array<string | null>>()
  for (const raw of existing) {
    const record = normalizeExistingManufacture(raw)
    if (record === null) continue
    const key = dayKey(productIdentity(index, record.productId), record.date, record.quantity)
    const batches = pool.get(key) ?? []
    batches.push(record.batchNumber)
    pool.set(key, batches)
  }
  /**
   * Use up one record of `base` whose batch is exactly `batch` — or, with `anyBatched`, the first
   * remaining record that has a batch. False when there is none.
   */
  const consume = (base: string, batch: string | null, anyBatched = false): boolean => {
    const batches = pool.get(base)
    if (!batches) return false
    const at = anyBatched ? batches.findIndex((b) => b !== null) : batches.indexOf(batch)
    if (at === -1) return false
    batches.splice(at, 1)
    return true
  }

  const prepared = infos.map(({ key, info }) => ({
    key,
    base: dayKey(productIdentity(index, info.product?.id), info.date, info.quantity),
    batch: normalizeBatch(info.batchNumber),
  }))

  // Deterministic, in the infos' (file/row) order, and MAXIMAL: as many new manufactures are
  // flagged as the records allow, so no record that could explain a row is left unused.
  // Compatibility per base: same batch, or either side without one. Steps, per base:
  //  1. exact batched pairs (B-77 ↔ B-77). Never worth breaking: the alternative (new B-77 → a
  //     no-batch record, record B-77 → a no-batch new row) uses two partners for the same two pairs.
  //  2. Now every leftover batched new row (N_B) can only take a no-batch record, and every
  //     leftover batched record (E_B) only a no-batch new row; no-batch rows (n0) and no-batch
  //     records (e0) pair with each other OR with those. Exact no-batch pairs are preferred, but
  //     only as many as keep the total maximal: d = max(e0 − N_B, n0 − E_B), clamped to
  //     [0, min(n0, e0)]. One more exact pair would take a record from a batched new row AND a row
  //     from a batched record — one pair gained, two lost. (Example: new [B-78, none] vs existing
  //     [B-77, none] → d = 0 and both match; pairing none ↔ none first would leave B-78 unmatched.)
  //  3. batched new rows take the remaining no-batch records;
  //  4. no-batch new rows take the remaining batched records.
  // After 2–4 no no-batch row and no-batch record are both left over, so nothing else can pair.
  // A record with a DIFFERENT batch is never used: a contradiction is not a duplicate.
  const matched = new Set<string>()
  const byBase = new Map<string, typeof prepared>()
  for (const entry of prepared) {
    const list = byBase.get(entry.base) ?? []
    list.push(entry)
    byBase.set(entry.base, list)
  }
  for (const [base, entries] of byBase) {
    for (const { key, batch } of entries) {
      if (batch !== null && consume(base, batch)) matched.add(key)
    }
    const records = pool.get(base) ?? []
    const noBatchNew = entries.filter(({ batch }) => batch === null)
    const batchedNewLeft = entries.filter(({ key, batch }) => batch !== null && !matched.has(key)).length
    const noBatchRecords = records.filter((b) => b === null).length
    const batchedRecordsLeft = records.length - noBatchRecords
    const exactNoBatch = Math.min(
      noBatchNew.length,
      noBatchRecords,
      Math.max(0, noBatchRecords - batchedNewLeft, noBatchNew.length - batchedRecordsLeft)
    )
    for (const { key } of noBatchNew.slice(0, exactNoBatch)) {
      if (consume(base, null)) matched.add(key)
    }
    for (const { key, batch } of entries) {
      if (matched.has(key) || batch === null) continue
      if (consume(base, null)) matched.add(key)
    }
    for (const { key } of noBatchNew) {
      if (matched.has(key)) continue
      if (consume(base, null, true)) matched.add(key)
    }
  }
  // Reported in file order, whatever order the bases were processed in.
  const existingMatch = new Set(prepared.filter(({ key }) => matched.has(key)).map(({ key }) => key))

  // Within a run "identical" means identical, batch included. Both twins are kept and flagged.
  const byTwinKey = new Map<string, string[]>()
  for (const { key, base, batch } of prepared) {
    const twinKey = `${base}${SEP}${batch === null ? 'none' : `batch:${batch}`}`
    const keys = byTwinKey.get(twinKey) ?? []
    keys.push(key)
    byTwinKey.set(twinKey, keys)
  }

  const inRunTwins = new Set<string>()
  for (const keys of byTwinKey.values()) {
    if (keys.length > 1) keys.forEach((key) => inRunTwins.add(key))
  }

  return { existingMatch, inRunTwins }
}

/** A prepared manufacture as the revision warning reads it (a PreparedManufacture satisfies it). */
export interface RevisionComparable {
  key: string
  info: Pick<ManufactureInfo, 'date'>
  /** Whether the FILE gave a time of day; without one, `info.date` is a placement, not a time. */
  draft: { date: Pick<ImportDate, 'hasTime'> }
}

/** The store business day of an instant as `yyyy-MM-dd` (lexically ordered). */
const businessDay = (store: string, date: Date): string => format(getStoreBusinessDate(store, date), 'yyyy-MM-dd')

/**
 * Keys of prepared manufactures that may predate the store's last stock revision: that revision
 * already counted their effect, so importing them double-counts. A warning, not a block.
 *
 *  - A row whose file cell carries a time of day is compared by the exact instant.
 *  - A row with only a date is placed at midday store time (or autoclose + 1) only so it lands on
 *    the right business day — it says nothing about when in the day it was made. It is flagged
 *    when its STORE business day is on or before the revision's: a revision at 09:00 on day D may
 *    have come after a manufacture of day D.
 */
export const revisionWarning = (
  items: RevisionComparable[],
  lastRevisionDate: Date | null,
  store: string
): string[] => {
  if (lastRevisionDate === null) return []
  if (!store) throw new Error('STORE_REQUIRED')
  const cutoff = lastRevisionDate.getTime()
  const revisionDay = businessDay(store, lastRevisionDate)
  return items
    .filter(({ info, draft }) =>
      draft.date.hasTime ? info.date.getTime() < cutoff : businessDay(store, info.date) <= revisionDay
    )
    .map(({ key }) => key)
}
